import { AppLogger } from '../appLogger';
import { AuthEvents } from './authEvents';

export type IdToken = {
  'custom:user_id': string;
  wam_token: string;
  aud?: string;
  auth_time?: number;
  'cognito:username'?: string;
  email?: string;
  exp: number;
  iat: number;
  iss: string;
  sub: string;
  stage: string;
  environment: string;
  region: string;
};

/**
 * JWT class
 * This class is used to parse the JWT token and get the payload
 * The payload contains claims. Claims are statements about an entity (typically, the user) and additional data.
 * Read more about the JWT payload here: https://jwt.io/introduction/
 * @param {string} jwtString - The JWT token string
 * @returns {IdToken} The payload of the JWT
 * @class
 */
export default class JWT {
  public jwtString: string;
  public jwtPayload: IdToken;

  constructor(jwtString: string) {
    this.jwtString = jwtString || '';
    this.jwtPayload = this.getJwtPayload();
  }

  /**
   * Returns the whole JWT string as it is
   * @returns
   */
  getJwtString() {
    return this.jwtString;
  }

  /**
   * Returns expiration time of the JWT
   * Unix timestamp (seconds since epoch) that identifies the expiration time on or after which the JWT must not be accepted for processing.
   * @returns
   */
  getExpiration() {
    return this.jwtPayload.exp;
  }

  correctTimeStamp(timestamp: number | string) {
    const numTimestamp = Number(timestamp); // Convert the timestamp to a number in case it's passed as a string
    const isSeconds = numTimestamp.toString().length === 10; // Check if the timestamp is in seconds (10 digits) or milliseconds (13 digits)
    const correctTimestamp = isSeconds ? numTimestamp * 1000 : numTimestamp; // If it's in seconds, multiply by 1000 to convert to milliseconds

    return correctTimestamp;
  }

  /**
   * Returns the issuer of the JWT
   * @returns
   */
  getIssuedAt() {
    return this.jwtPayload.iat;
  }

  /**
   * A JSON Web Token (JWT) consists of three parts separated by dots: header, payload, and signature.
   *  * Usually the payload contains the following information:
   * - Issuer (iss) The issuer of the token e.g 'https://api.test.lifeworks.com'
   * - Subject (sub) Whom the token refers to e.g user id or cognito username
   * - Audience (aud)  Identifies the recipients that the JWT is intended for e.g 'web'
   * - Expiration Unix Time (exp)
   * - Issued At (iat) Unix Time
   * - JWT ID (jti)
   * - Custom claims
   * @returns {IdToken} The payload of the JWT
   */
  getJwtPayload(): IdToken {
    try {
      // The middle part of the JWT is the payload, which contains the claims.
      const jwtPayload = this.jwtString.split('.')[1];
      let decodedPayload: string;

      try {
        // base64 decode the payload
        decodedPayload = atob(jwtPayload as string);
      } catch (decodeError) {
        AppLogger.error(
          'Error decoding base64 JWT payload',
          decodeError,
          jwtPayload,
        );
        const errorString = 'Invalid base64 string in JWT payload';
        AuthEvents.publish('auth:error', { error: errorString });
        throw new Error(errorString);
      }

      const alteredJWTPayload = JSON.parse(decodedPayload);
      // Converts the timestamp to milliseconds
      alteredJWTPayload.exp = this.correctTimeStamp(alteredJWTPayload.exp);
      return alteredJWTPayload;
    } catch (err) {
      AppLogger.error('Error parsing JWT payload', err);
      throw err;
    }
  }
}
